import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItem,
  ListItemText,
  Stack,
} from '@mui/material'
import { ApiKey, UpdateApiKeyDocument } from '../../../generated/graphql'
import { SwitchInput } from '../../../inputs/switch-input'
import { useForm } from 'react-hook-form'
import { TextFieldInput } from '../../../inputs/text-field-input'
import { AutocompleteInput } from '../../../inputs/autocomplete-input'
import { useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { enqueueSnackbar } from 'notistack'
import { getErrorMessages } from '../../../utils/error-mapping'

type Props = {
  apiKey: ApiKey
  onClose: () => void
  open: boolean
}

export const EditApiKeyDialog = (props: Props) => {
  const { apiKey, onClose, open } = props
  const { t } = useTranslation(['api_keys', 'shared'])
  const { control, handleSubmit, reset } = useForm({
    values: {
      name: apiKey.name,
      enabled: apiKey.enabled,
      ip_restrictions: apiKey.ip_restrictions,
    },
  })
  const [updateApiKey, { loading: isUpdating }] =
    useMutation(UpdateApiKeyDocument)

  const onSubmit = async (
    data: Pick<ApiKey, 'name' | 'enabled' | 'ip_restrictions'>,
  ) => {
    try {
      await updateApiKey({
        variables: {
          apiKeyId: apiKey.id,
          name: data.name,
          enabled: data.enabled,
          ipRestrictions: data.ip_restrictions,
        },
      })
      onClose()
    } catch (error) {
      const errorMessages = getErrorMessages(error)
      enqueueSnackbar(
        t('api_keys:update_api_key_error', {
          error: errorMessages[0],
        }),
        {
          variant: 'error',
        },
      )
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t('api_keys:edit_api_key')}</DialogTitle>

      <DialogContent
        sx={{
          minWidth: 400,
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)} id="edit-api-key-form">
          <Stack
            sx={{
              gap: 2,
            }}
          >
            <Stack>
              <ListItem
                component="div"
                sx={{
                  '&.MuiListItem-root': {
                    padding: 0,
                    margin: 0,
                  },
                }}
                disableGutters
                disablePadding
                secondaryAction={
                  <SwitchInput
                    name="enabled"
                    checked={apiKey.enabled}
                    control={control}
                    aria-label={t('api_keys:enabled')}
                  />
                }
              >
                <ListItemText
                  primary={t('api_keys:enabled')}
                  secondary={t('api_keys:status_description')}
                />
              </ListItem>
              <Divider />
            </Stack>
            <TextFieldInput
              name="name"
              label={t('api_keys:name')}
              control={control}
              rules={{
                required: t('api_keys:name_required'),
              }}
              helperText={t('api_keys:name_description')}
            />
            <AutocompleteInput
              name="ip_restrictions"
              label={t('api_keys:ip_restrictions')}
              control={control}
              options={[]}
              multiple
              freeSolo
              textFieldProps={{
                helperText: t('api_keys:ip_restrictions_description'),
              }}
            />
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            reset()
            onClose()
          }}
          color="secondary"
        >
          {t('shared:action.cancel')}
        </Button>
        <Button
          color="primary"
          type="submit"
          disabled={isUpdating}
          form="edit-api-key-form"
        >
          {t('shared:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
